// Rope: hangs a stone or ball from a hook. Cut by a button: act: { cut: '<id>' }
import type Matter from 'matter-js';
import { defineObj } from '../core/registry';
import { rr, C } from '../core/draw-helpers';
import type { Ctx2D, GameBody, Vec } from '../core/types';
import { stoneBody } from './stone';

export interface Rope { id?: string; c: Matter.Constraint; cut: boolean; ax: number; ay: number; body: GameBody; cutT: number }
declare module '../core/types' {
  interface SimState { ropes: Rope[] }
  // level data: placed by its anchor (ax, ay) with a stone or ball hanging below (hang), not by x/y
  interface ObjDataTypes { rope: { id?: string; ax: number; ay: number; hang: Hang } }
}

export function drawScissors(ctx: Ctx2D, x: number, y: number, closed: boolean | number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(Math.PI);
  const o = closed ? 0.05 : 0.35;
  ctx.strokeStyle = '#8B84A0'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  [-o, o].forEach(a => { ctx.save(); ctx.rotate(a); ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(22, 0); ctx.stroke(); ctx.beginPath(); ctx.arc(-7, 0, 5, 0, 7); ctx.strokeStyle = '#FF7A7A'; ctx.stroke(); ctx.strokeStyle = '#8B84A0'; ctx.restore(); });
  ctx.restore();
}

export function drawRope(ctx: Ctx2D, R: { ax: number; ay: number; cut: boolean; cutT: number; body: { position: Vec; circleRadius?: number } }, t: number) {
  const b = R.body;
  // hook
  ctx.save();
  ctx.fillStyle = C.ink; rr(ctx, R.ax - 14, R.ay - 8, 28, 8, 4); ctx.fill();
  ctx.strokeStyle = C.ink; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(R.ax, R.ay + 5, 5, Math.PI * 1.5, Math.PI * 1.1, true); ctx.stroke();
  const top = { x: R.ax, y: R.ay + 8 };
  ctx.lineWidth = 5; ctx.lineCap = 'round'; ctx.strokeStyle = '#C58B4A';
  if (!R.cut) {
    const end = { x: b.position.x, y: b.position.y - b.circleRadius! };
    ctx.beginPath(); ctx.moveTo(top.x, top.y); ctx.lineTo(end.x, end.y); ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,.4)'; ctx.lineWidth = 2; ctx.setLineDash([4, 5]); ctx.beginPath(); ctx.moveTo(top.x, top.y); ctx.lineTo(end.x, end.y); ctx.stroke(); ctx.setLineDash([]);
    // scissors waiting
    drawScissors(ctx, top.x + (end.x - top.x) * 0.35 + 18, top.y + (end.y - top.y) * 0.35, 0);
  } else {
    const k = Math.min(1, (t - R.cutT) / 20);
    ctx.beginPath(); ctx.moveTo(top.x, top.y); ctx.quadraticCurveTo(top.x + 10 * k, top.y + 30, top.x + 4, top.y + 50 - 10 * k); ctx.stroke();
    drawScissors(ctx, top.x + 18, top.y + 60, 1);
  }
  ctx.restore();
}

/** What hangs from the rope: a stone (t: 'stone') or a ball, and where */
export interface Hang { t?: string; x: number; y: number; r?: number }

export const rope = defineObj({
  phase: 'core', layer: 'mid', z: -2, thumb: 4,
  create(api, o) {
    const S = api.S, hb = o.hang as Hang, ax = o.ax as number, ay = o.ay as number;
    let b: GameBody;
    if (hb.t === 'stone') b = stoneBody(api, hb.x, hb.y);
    else { b = api.add(api.Bodies.circle(hb.x, hb.y, hb.r || 15, api.heroOpts('hero'))); b.kind = 'hero'; b.r = hb.r || 15; S.heroes.push(b); S.need = (api.level.goal!.count) || S.heroes.length; }
    S.dyn.push(b);
    const c = api.Constraint.create({ pointA: { x: ax, y: ay }, bodyB: b, pointB: { x: 0, y: -(b.circleRadius || 15) }, stiffness: 0.9, damping: 0.1 });
    api.Composite.add(api.world, c);
    const R: Rope = { id: o.id as string | undefined, c, cut: false, ax, ay, body: b, cutT: 0 };
    S.ropes.push(R);
    return R;
  },
  // the rope is drawn with the simulation's time (S.t)
  draw: (ctx, st, t, g, S) => drawRope(ctx, st, S.t),
  blockers: o => [{ x: (o.hang as Hang).x, y: (o.hang as Hang).y, r: 30 }],
  system: {
    init: api => { api.S.ropes = []; },
    act: {
      cut(api, id) {
        const S = api.S, r = S.ropes.find(k => k.id === id);
        if (r && !r.cut) { r.cut = true; r.cutT = S.t; api.Composite.remove(api.world, r.c); S.events.push({ e: 'snip', x: r.ax, y: r.ay + 30 }); }
      },
    },
  },
});
